import { Page } from '@looker/components';
import React, { type FC, lazy, Suspense, useEffect, useRef } from 'react';
import { Redirect, Route, Switch, useLocation } from 'react-router-dom';
import { z } from 'zod';
import { CowWaveLoader } from '../components/CowWaveLoader';
import { QueryResolutionGuard } from '../components/QueryResolutionGuard';
import { ROUTES } from '../helpers';
import { useAuth } from '../hooks/api/auth';
import { useHandleDeeplink } from '../hooks/useHandleDeeplink';
import { usePageAnimation } from '../hooks/usePageAnimation';
import { usePermissions } from '../hooks/usePermissions';
import { identifyUser } from '../lib/eventsLogger';
import WorkspaceDataProvider from '../providers/WorkspaceDataProvider';
import { useUserData } from './GenomeWorkspaceView/LeftView/hooks/useUserData';
import { ProtectedRoute } from './ProtectedRoute';

// Lazy load workspace views - each becomes a separate chunk
const GenomeWorkspaceView = lazy(() =>
  import('./GenomeWorkspaceView/GenomeWorkspaceView').then((m) => ({
    default: m.GenomeWorkspaceView,
  })),
);
const DTCWorkspaceView = lazy(() =>
  import('./DTCWorkspaceView/DTCWorkspaceView').then((m) => ({
    default: m.DTCWorkspaceView,
  })),
);
const ListMatchingWorkspaceView = lazy(() =>
  import('./ListMatchingWorkspaceView/ListMatchingWorkspaceView').then((m) => ({
    default: m.ListMatchingWorkspaceView,
  })),
);
const ListMatchingConsentWorkspaceView = lazy(() =>
  import('./ListMatchingWorkspaceView/ListMatchingConsentWorkspaceView').then((m) => ({
    default: m.ListMatchingConsentWorkspaceView,
  })),
);
const MedscapeSoftMatchWorkspaceView = lazy(() =>
  import('./MedscapeSoftMatchWorkspaceView/MedscapeSoftMatchWorkspaceView').then((m) => ({
    default: m.MedscapeSoftMatchWorkspaceView,
  })),
);
const MediaEffectivenessDataLoader = lazy(() =>
  import('./MediaEffectivenessWorkspaceView/MediaEffectivenessDataLoader').then((m) => ({
    default: m.MediaEffectivenessDataLoader,
  })),
);
const MediaEffectivenessWorkspaceView = lazy(() =>
  import('./MediaEffectivenessWorkspaceView/MediaEffectivenessWorkspaceView').then((m) => ({
    default: m.MediaEffectivenessWorkspaceView,
  })),
);
const ListMatchDataLoader = lazy(() =>
  import('./ListMatchingWorkspaceView/ListMatchDataLoader').then((m) => ({ default: m.ListMatchDataLoader })),
);
const ThrotleWorkspaceView = lazy(() =>
  import('./ThrotleWorkspaceView/ThrotleWorkspaceView').then((m) => ({
    default: m.ThrotleWorkspaceView,
  })),
);
const WorkspaceSelectionView = lazy(() =>
  import('./WorkspaceSelectionView/WorkspaceSelectionView').then((m) => ({
    default: m.WorkspaceSelectionView,
  })),
);
const CreateNewWorkspaceView = lazy(() =>
  import('./CreateNewWorkspaceView/CreateNewWorkspaceView').then((m) => ({
    default: m.CreateNewWorkspaceView,
  })),
);
const WorkspaceAdvertiserSelectionView = lazy(() =>
  import('./WorkspaceAdvertiserSelectionView/WorkspaceAdvertiserSelectionView').then((m) => ({
    default: m.WorkspaceAdvertiserSelectionView,
  })),
);
const CreateHcpAudienceExpansionView = lazy(() =>
  import('./CreateHcpAudienceExpansionView').then((m) => ({
    default: m.CreateHcpAudienceExpansionView,
  })),
);
const CreateListMatchingView = lazy(() =>
  import('./CreateListMatchingView').then((m) => ({
    default: m.CreateListMatchingView,
  })),
);
const HCPSiteWorkspaceView = lazy(() =>
  import('./HCPSiteWorkspaceView/HCPSiteWorkspaceView').then((m) => ({
    default: m.HCPSiteWorkspaceView,
  })),
);
const BrandExplorerWorkspaceView = lazy(() =>
  import('./BrandExplorerWorkspaceView/BrandExplorerWorkspaceView').then((m) => ({
    default: m.BrandExplorerWorkspaceView,
  })),
);
const QueryParamsView = lazy(() => import('./QueryParamsView').then((m) => ({ default: m.QueryParamsView })));
const AIAssistantDebugTool = lazy(() =>
  import('./AIAssistantDebugTool').then((m) => ({ default: m.AIAssistantDebugTool })),
);

export const MainView: FC = () => {
  const location = useLocation();

  const { userData, saveUserData, setUserEmail } = useUserData();
  const { accountId, accountName, email: permissionEmail } = usePermissions();
  const containerRef = useRef<HTMLDivElement>(null);

  usePageAnimation({ key: location.pathname, containerRef });
  useAuth();
  useHandleDeeplink();

  useEffect(() => {
    if (permissionEmail?.trim() && !userData.userEmail) {
      setUserEmail(permissionEmail.trim());
    }
  }, [permissionEmail, setUserEmail, userData.userEmail]);

  useEffect(() => {
    if (userData?.userID) {
      identifyUser(String(userData.userID), {
        email: userData.userEmail || permissionEmail?.trim(),
        last_seen: new Date().toISOString(),
        account_id: accountId,
        account_name: accountName,
      });
    } else {
      void saveUserData();
    }
  }, [userData.userID, userData.userEmail, accountId, accountName, saveUserData, permissionEmail]);

  return (
    <Page height="100vh" data-tag={__BUILD_TAG__} data-commit={__COMMIT_HASH__} data-testid="main-view-page">
      <div ref={containerRef} style={{ opacity: 1 }}>
        <Suspense fallback={<CowWaveLoader />}>
          <Switch>
            <Route exact path={ROUTES.QUERY_PARAMS_TEST}>
              <QueryParamsView />
            </Route>

            <ProtectedRoute exact path={ROUTES.AI_ASSISTANT_DEBUG_TOOL}>
              <AIAssistantDebugTool />
            </ProtectedRoute>

            <Route exact path={ROUTES.LINK_MEDIA_EFFECTIVENESS}>
              <QueryResolutionGuard requiredKeys={['campaignID', 'accountID', 'advertiserID']}>
                <MediaEffectivenessDataLoader>
                  <MediaEffectivenessWorkspaceView isEphemeral />
                </MediaEffectivenessDataLoader>
              </QueryResolutionGuard>
            </Route>

            <Route exact path={ROUTES.LINK_LIST_MATCHING}>
              <QueryResolutionGuard
                schema={z
                  .object({
                    advertiserID: z.string(),
                    accountID: z.string(),
                    workspaceID: z.string().optional(),
                    npiListID: z.string().optional(),
                  })
                  .refine((data) => data.workspaceID || data.npiListID, {
                    message: 'Missing required parameter: either workspaceID or npiListID must be provided.',
                  })}
              >
                {(params) => <ListMatchDataLoader params={params} />}
              </QueryResolutionGuard>
            </Route>

            <ProtectedRoute
              exact
              path={ROUTES.WORKSPACE_NEW_ADVERTISER_SELECTION}
              render={({ match }) => (
                <WorkspaceAdvertiserSelectionView workspaceTypeId={Number(match.params.workspaceTypeId)} />
              )}
            />

            <ProtectedRoute exact path={ROUTES.WORKSPACE_NEW}>
              <CreateNewWorkspaceView />
            </ProtectedRoute>

            <ProtectedRoute exact path={ROUTES.WORKSPACE_HCP_NEW}>
              <CreateHcpAudienceExpansionView />
            </ProtectedRoute>

            <ProtectedRoute
              exact
              path={ROUTES.WORKSPACE_HCP_VIEW}
              render={({ match }) => (
                <WorkspaceDataProvider workspaceId={Number(match.params.workspaceId)}>
                  <GenomeWorkspaceView />
                </WorkspaceDataProvider>
              )}
            />

            <ProtectedRoute
              exact
              path={ROUTES.WORKSPACE_THROTLE_VIEW}
              render={({ match }) => (
                <WorkspaceDataProvider workspaceId={Number(match.params.workspaceId)}>
                  <ThrotleWorkspaceView />
                </WorkspaceDataProvider>
              )}
            />

            <ProtectedRoute
              exact
              path={ROUTES.WORKSPACE_LIST_MATCHING_NEW}
              render={({ match }) => <CreateListMatchingView workspaceId={Number(match.params.workspaceId)} />}
            />

            <ProtectedRoute
              exact
              path={ROUTES.WORKSPACE_LIST_MATCHING_VIEW}
              render={({ match }) => (
                <WorkspaceDataProvider workspaceId={Number(match.params.workspaceId)}>
                  <ListMatchingWorkspaceView />
                </WorkspaceDataProvider>
              )}
            />

            <ProtectedRoute
              exact
              path={ROUTES.WORKSPACE_LIST_MATCHING_CONSENT_VIEW}
              render={({ match }) => (
                <WorkspaceDataProvider workspaceId={Number(match.params.workspaceId)}>
                  <ListMatchingConsentWorkspaceView />
                </WorkspaceDataProvider>
              )}
            />

            <ProtectedRoute
              exact
              path={ROUTES.WORKSPACE_MEDIA_EFFECTIVENESS_VIEW}
              render={({ match }) => (
                <WorkspaceDataProvider workspaceId={Number(match.params.workspaceId)}>
                  <MediaEffectivenessWorkspaceView />
                </WorkspaceDataProvider>
              )}
            />

            <ProtectedRoute
              exact
              path={ROUTES.WORKSPACE_DTC_VIEW}
              render={({ match }) => (
                <WorkspaceDataProvider workspaceId={Number(match.params.workspaceId)}>
                  <DTCWorkspaceView />
                </WorkspaceDataProvider>
              )}
            />

            <ProtectedRoute
              exact
              path={ROUTES.WORKSPACE_HCP_SITE_ANALYTICS_VIEW}
              render={({ match }) => (
                <WorkspaceDataProvider workspaceId={Number(match.params.workspaceId)}>
                  <HCPSiteWorkspaceView />
                </WorkspaceDataProvider>
              )}
            />

            <ProtectedRoute
              exact
              path={ROUTES.WORKSPACE_BRAND_EXPLORER_VIEW}
              render={({ match }) => (
                <WorkspaceDataProvider workspaceId={Number(match.params.workspaceId)}>
                  <BrandExplorerWorkspaceView />
                </WorkspaceDataProvider>
              )}
            />

            <ProtectedRoute
              exact
              path={ROUTES.WORKSPACE_MEDSCAPE_SOFT_MATCH_VIEW}
              render={({ match }) => (
                <WorkspaceDataProvider workspaceId={Number(match.params.workspaceId)}>
                  <MedscapeSoftMatchWorkspaceView />
                </WorkspaceDataProvider>
              )}
            />

            <ProtectedRoute exact path={ROUTES.WORKSPACES}>
              <WorkspaceSelectionView />
            </ProtectedRoute>

            {/* Preserve search params so deeplink query strings survive the redirect */}
            <Route
              render={({ location: loc }) => <Redirect to={{ pathname: ROUTES.WORKSPACES, search: loc.search }} />}
            />
          </Switch>
        </Suspense>
      </div>
    </Page>
  );
};
export default React.memo(MainView);
